import React, { useEffect, useCallback, lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { isMobile } from 'react-device-detect';
import Loader from './components/Loader';
import EventExpired from './components/EventExpire';
import Error from './views/Error';
import './assets/css/app.css';
import './assets/css/pip.css';
import { APP_VIEW, EVENT_TYPE, STREAM_TYPE } from './constants';
import Welcome from './views/Welcome';
import EventEnd from './views/EventEnd';
import { proceedHandler } from './store/app/actions';
import VideoElement from './utils/video-element';
import AppNotification from './components/Notification';
import Pip from './components/Pip';
import { handleResize, setAppError, toggleFullScreen, setGoLive } from './store/app';
import ErrorBoundary from './ErrorBoundary';
import screenFull from 'screenfull';
import { isIosDevice } from './utils';

const HlsLive = lazy(() => import('./views/HlsLive'));
const HlsRecording = lazy(() => import('./views/HlsRecording'));
const CastLive = lazy(() => import('./views/CastLive'));

let mql;
function App({
  ctrl,
  proceedClick,
  loader,
  autoPlayBehavior,
  userName,
  view,
  activeTab,
  eventType,
  poster,
  mode,
  handleModeResize,
  showControl,
  limitedViewing,
  theme,
  setAppError,
  chatLoaded,
  forceFsMobile,
  toggleFullScreen,
  castMode,
  pipMode,
  fixedTabControls,
  crop,
  hideTab,
  mobileposter,
  clientBgVideo,
  setGoLive,
  goLive,
  eventStatus,
  streamType,
  headless,
  showCommentsRec,
  adActive,
}) {
  const windowHeight = window.screen.height;
  const [isInputClick, setIsInputClick] = React.useState(false);
  const [isKeyBoardOpen, setKeyBoardOpen] = React.useState(false);
  const videoEl = React.useRef(new VideoElement(true));
  const adVideoEl = React.useRef(new VideoElement(true));

  const onProceed = async (force, displayName) => {
    videoEl.current = new VideoElement();
    adVideoEl.current = new VideoElement();
    if (eventType !== EVENT_TYPE.WB_LIVE && isMobile && forceFsMobile && !isIosDevice()) {
      screenFull.toggle(document.body);
      toggleFullScreen(true);
    }
    await videoEl.current.setAutoPlay();
    proceedClick(force, displayName);
  };

  const loadTheme = async () => {
    try {
      if (theme === 'tp') {
        await import(/* webpackChunkName: "tp-theme" */ `./themes/tp`);
      }
      if (theme === 'et') {
        await import(/* webpackChunkName: "et-theme" */ `./themes/et`);
      }
      if (theme) {
        document.getElementById('root').classList.add(theme);
      }
      if (headless) {
        document.getElementById('root').classList.add('headless');
      }
    } catch (err) {
      console.error('load theme', err);
    }
  };

  useEffect(() => {
    loadTheme();
    setTimeout(() => {
      handleModeResize();
    }, 100);

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        handleModeResize();
      }, 100);
    });
    try {
      mql = window.matchMedia('(orientation: portrait)');
      mql.addEventListener('change', handleModeResize);
    } catch (err) {
      console.log('match media not supported');
    }
    return () => {
      window.removeEventListener('orientationchange', handleModeResize);
      try {
        if (mql) {
          mql.removeEventListener('change', handleModeResize);
        }
      } catch (err) {
        console.log('match media not supported');
      }
    };
    //eslint-disable-next-line
  }, []);

  const getMainView = () => {
    switch (eventType) {
      case EVENT_TYPE.HLS_LIVE:
        if (castMode) {
          return <CastLive ctrl={ctrl} videoEl={videoEl.current} adVideoEl={adVideoEl.current} />;
        }
        return <HlsLive ctrl={ctrl} videoEl={videoEl.current} adVideoEl={adVideoEl.current} />;
      case EVENT_TYPE.HLS_RECORDING:
        return <HlsRecording ctrl={ctrl} videoEl={videoEl.current} />;
      default:
        return null;
    }
  };

  const getView = () => {
    if (view === APP_VIEW.MAIN) {
      return <Suspense fallback={<Loader />}>{getMainView()}</Suspense>;
    }
    const viewComponent = () => {
      switch (view) {
        case APP_VIEW.ERROR:
          return <Error />;
        case APP_VIEW.WELCOME:
          return <Welcome ctrl={ctrl} onProceed={onProceed} poster={poster} />;
        case APP_VIEW.END:
          return <EventEnd poster={poster} ctrl={ctrl} />;
        default:
          return null;
      }
    };
    return viewComponent();
  };

  const getBgStyles = () => {
    if (eventType === EVENT_TYPE.HLS_LIVE && view === APP_VIEW.MAIN && clientBgVideo) {
      return {
        backgroundColor: 'black',
      };
    }
    return {
      backgroundImage: `url("${poster}")`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center center',
    };
  };

  const getEventExpiredUI = () => {
    return <EventExpired ctrl={ctrl} />;
  };

  const getClassName = () => {
    let className = 'main pageBg';
    if (showControl) {
      className += ' showControls';
    } else {
      className += ' hideControls';
    }
    if (eventType === EVENT_TYPE.WB_LIVE || eventType === EVENT_TYPE.WB_RECORDING) {
      className += hideTab ? ' tabHide' : ' showTabs multiHostRHS';
    }
    if (eventType !== EVENT_TYPE.WB_LIVE && (activeTab || fixedTabControls)) {
      className += ' showTabControls';
    }
    if (mode === 'portrait') {
      className += ' device-portrait';
    } else {
      className += ' device-landscape ';
    }
    if (isMobile) {
      className += ' mobile';
    }
    if (isMobile && mode === 'landscape') {
      className += ' device-landscape-mobile';
    }
    if (isMobile && mode === 'landscape' && activeTab) {
      className += ' landscapeControls';
    }
    if (castMode) {
      className += ' google-cast';
    }
    if (poster === mobileposter) {
      className += ' posterPor';
    }
    if (view === APP_VIEW.WELCOME) {
      className += ' welcommWeb';
    }
    if (view === APP_VIEW.END) {
      className += ' eventEndCont';
    }
    if (eventType === EVENT_TYPE.HLS_RECORDING || eventType === EVENT_TYPE.WB_RECORDING) {
      className += ' recordingOuter';
    }
    if (eventType === EVENT_TYPE.HLS_RECORDING && showCommentsRec) {
      className += ' showCom';
    }
    if (eventType === EVENT_TYPE.WB_LIVE && isMobile && mode === 'portrait') {
      const element = document.getElementsByClassName('mainDiv')[0];
      if (element && element.classList.contains('wbMoreUser')) {
        className += ' wbMoreUser';
      }
    }
    if (streamType === STREAM_TYPE.PORTRAIT && eventType === EVENT_TYPE.HLS_LIVE && isMobile && mode === 'portrait') {
      className += ' dVideo-portrait';
    } else if (streamType === STREAM_TYPE.PORTRAIT && eventType === EVENT_TYPE.HLS_LIVE && mode === 'landscape') {
      className += ' wPortrait';
    }

    return className;
  };

  useEffect(() => {
    if (isMobile && mode === 'portrait' && windowHeight <= 800 && isInputClick === true) {
      setKeyBoardOpen(true);
    } else {
      setKeyBoardOpen(false);
    }
    //eslint-disable-next-line
  }, [mode, isMobile, isInputClick]);

  const handleEvent = useCallback((event) => {
    var emojiElement = document.getElementById('emoji-list');
    if (
      event.target.id === 'chat-inputbox' ||
      event.target.id === 'send-btn' ||
      (emojiElement && emojiElement.contains(event.srcElement)) ||
      !event.srcElement.closest('.js-emojiBtn')
    ) {
      setIsInputClick(true);
    } else {
      setIsInputClick(false);
    }
  }, []);

  useEffect(() => {
    if (chatLoaded) {
      document.body.addEventListener('click', handleEvent);
    }
    //eslint-disable-next-line
  }, [chatLoaded]);

  useEffect(() => {
    setGoLive(goLive);
    //eslint-disable-next-line
  }, [goLive]);

  useEffect(() => {
    return () => {
      if (handleEvent) {
        document.body.removeEventListener('click', handleEvent);
      }
    };
    //eslint-disable-next-line
  }, []);

  return (
    <ErrorBoundary>
      {loader ? <Loader /> : null}
      <div
        className={
          getClassName() +
          ' mainDiv ' +
          (isKeyBoardOpen ? ' keyboardVisible' : ' ') +
          (pipMode ? 'pipCont' : '') +
          (crop && mode === 'portrait' ? ' momentsTab' : '') +
          (adActive ? ' adActive' : '')
        }
        style={getBgStyles()}
      >
        {getView()}
        <Pip />
        {view && limitedViewing && view !== APP_VIEW.END && getEventExpiredUI()}
      </div>
      <AppNotification />
    </ErrorBoundary>
  );
}

const mapStateToProps = (state) => {
  return {
    eventType: state.app.eventType,
    loader: state.app.loader,
    view: state.app.view,
    poster: state.app?.poster,
    mode: state.app.mode,
    showControl: state.app.showControl,
    limitedViewing: state.app.limitedViewing,
    theme: state.app.apiJsonConfig.theme,
    activeTab: state.app.activeTab,
    chatLoaded: state.app.chatLoaded,
    clickSet: state.app.clickSet,
    autoPlayBehavior: state.app.autoPlayBehavior,
    userName: state.app.config.jwtData.name,
    forceFsMobile: state.app.config.forceFsMobile,
    castMode: state.app.config.castMode,
    pipMode: state.app.pipMode,
    fixedTabControls: state.app.fixedTabControls,
    crop: state.app.crop,
    hideTab: state.app.hideTab,
    mobileposter: state.app.welcomeData?.meta.mobileposter,
    clientBgVideo: state.app.apiJsonConfig.blurBG,
    eventStatus: state.app.eventStatus,
    goLive: state.app.goLive,
    streamType: state.app.welcomeData?.meta.sttype,
    headless: state.app.config.headless,
    showCommentsRec: state.app.showCommentsRec,
    adActive: state.app.adActive,
  };
};

const mapDispatchToProps = {
  proceedClick: proceedHandler,
  handleModeResize: handleResize,
  setAppError: setAppError,
  toggleFullScreen,
  setGoLive,
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
